'use client'

import { useEffect, useRef } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import { cn } from '../lib/utils'
import { loadingIndicatorMorphRings } from './loading-indicator-morphs'
import { softBurst, cookie9, pentagon, pill, sunny, cookie4, oval } from './shapes'

// MD3 indeterminate sequence and motion constants:
// https://android.googlesource.com/platform/frameworks/support/+/androidx-main/compose/material3/material3/src/commonMain/kotlin/androidx/compose/material3/LoadingIndicator.kt
export const loadingIndicatorShapes = [softBurst, cookie9, pentagon, pill, sunny, cookie4, oval]
const shapes = loadingIndicatorShapes

/** The path of transition `index` (shape index to the next) at `progress`: flubber's interpolation (point-wise lerp
 * of two aligned rings, the original paths at the ends) over rings precomputed by loading-indicator-morphs.generate.ts. */
export function loadingIndicatorMorph(index: number, progress: number): string {
  if (progress < 1e-4) return shapes[index].path
  if (1 - progress < 1e-4) return shapes[(index + 1) % shapes.length].path
  const [from, to] = loadingIndicatorMorphRings[index]
  let d = 'M'
  for (let i = 0; i < from.length; i += 2) {
    d += `${i ? 'L' : ''}${from[i] + progress * (to[i] - from[i])},${from[i + 1] + progress * (to[i + 1] - from[i + 1])}`
  }
  return `${d}Z`
}

export type LoadingIndicatorProps = ComponentPropsWithoutRef<'span'> & {
  variant?: 'default' | 'contained'
}

/** MD3 expressive indeterminate loader. Size and colors can be overridden with className. */
export function LoadingIndicator({
  variant = 'default',
  className,
  'aria-label': label = 'Loading',
  ...props
}: LoadingIndicatorProps) {
  const pathRef = useRef<SVGPathElement>(null)
  const rotationRef = useRef<SVGGElement>(null)

  useEffect(() => {
    const path = pathRef.current
    const rotation = rotationRef.current
    if (!path || !rotation) {
      throw new Error('LoadingIndicator SVG refs must be mounted before animation starts')
    }
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    let frame = 0
    let start: number | undefined

    const tick = (now: number) => {
      start ??= now
      const elapsed = now - start
      const step = Math.floor(elapsed / 650)
      const time = (elapsed % 650) / 1000
      // Unit-mass spring: stiffness 200, damping ratio 0.6.
      const omega = Math.sqrt(200)
      const damped = omega * 0.8
      const spring = 1 - Math.exp(-0.6 * omega * time) *
        (Math.cos(damped * time) + 0.75 * Math.sin(damped * time))
      const progress = Math.min(1, Math.max(0, spring))
      path.setAttribute('d', loadingIndicatorMorph(step % shapes.length, progress))
      const angle = 90 + step * 90 + spring * 90 + elapsed * 360 / 4666
      rotation.setAttribute('transform', `rotate(${angle % 360})`)
      frame = requestAnimationFrame(tick)
    }

    const update = () => {
      cancelAnimationFrame(frame)
      start = undefined
      if (media.matches || document.hidden) {
        path.setAttribute('d', softBurst.path)
        rotation.setAttribute('transform', 'rotate(90)')
      } else {
        frame = requestAnimationFrame(tick)
      }
    }
    update()
    media.addEventListener('change', update)
    document.addEventListener('visibilitychange', update)
    return () => {
      cancelAnimationFrame(frame)
      media.removeEventListener('change', update)
      document.removeEventListener('visibilitychange', update)
    }
  }, [])

  return (
    <span
      {...props}
      role="progressbar"
      aria-label={label}
      data-slot="loading-indicator"
      data-variant={variant}
      className={cn(
        'inline-flex size-12 shrink-0 items-center justify-center rounded-full align-middle',
        variant === 'contained' ? 'bg-primary/16 text-primary' : 'text-primary',
        className,
      )}
    >
      <svg viewBox="0 0 48 48" className="size-full" aria-hidden="true" focusable="false">
        <g transform="translate(24 24)">
          <g ref={rotationRef} transform="rotate(90)">
            <g transform="scale(0.1) translate(-190 -190)">
              <path ref={pathRef} d={softBurst.path} fill="currentColor" />
            </g>
          </g>
        </g>
      </svg>
    </span>
  )
}
